<template>
  <div class="knowledge-base-manager">
    <div class="container">
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link to="/admin">Admin</router-link></li>
          <li class="is-active">
            <router-link to="/admin/knowledge-base" aria-current="true">AI Knowledge Base</router-link>
          </li>
        </ul>
      </nav>

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">AI Knowledge Base Management</h1>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <button 
              class="button is-primary" 
              @click="openUploadModal"
            >
              <font-awesome-icon icon="plus" class="mr-2" />
              Add Document
            </button>
          </div>
          <div class="level-item">
            <button 
              class="button is-info" 
              @click="rebuildIndex"
              :class="{ 'is-loading': rebuildingIndex }"
              :disabled="rebuildingIndex"
            >
              <font-awesome-icon icon="sync-alt" class="mr-2" />
              Rebuild Index
            </button>
          </div>
          <div class="level-item">
            <button 
              class="button is-light" 
              @click="checkIndexStats"
            >
              <font-awesome-icon icon="chart-bar" class="mr-2" />
              Index Stats
            </button>
          </div>
        </div>
      </div>

      <!-- Index Statistics Card -->
      <div v-if="indexStats" class="card mb-5">
        <div class="card-header">
          <p class="card-header-title">
            <font-awesome-icon icon="database" class="mr-2" />
            Index Statistics
          </p>
        </div>
        <div class="card-content">
          <div class="columns">
            <div class="column">
              <div class="has-text-centered">
                <p class="title is-4">{{ indexStats.total_chunks }}</p>
                <p class="subtitle is-6">Total Chunks</p>
              </div>
            </div>
            <div class="column">
              <div class="has-text-centered">
                <p class="title is-4">
                  <span :class="indexStats.index_exists ? 'has-text-success' : 'has-text-danger'">
                    <font-awesome-icon :icon="indexStats.index_exists ? 'check-circle' : 'times-circle'" />
                  </span>
                </p>
                <p class="subtitle is-6">Index Status</p>
              </div>
            </div>
            <div class="column">
              <div class="has-text-centered">
                <p class="title is-6">{{ indexStats.embedding_model }}</p>
                <p class="subtitle is-6">Embedding Model</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents Table -->
      <div class="card">
        <div class="card-header">
          <p class="card-header-title">
            <font-awesome-icon icon="file-alt" class="mr-2" />
            Knowledge Base Documents
          </p>
        </div>
        <div class="card-content">
          <div v-if="loading" class="has-text-centered">
            <font-awesome-icon icon="spinner" spin class="fa-2x" />
            <p class="mt-2">Loading documents...</p>
          </div>

          <div v-else-if="documents.length === 0" class="has-text-centered">
            <font-awesome-icon icon="folder-open" class="fa-3x has-text-grey-light mb-3" />
            <p class="title is-5">No documents uploaded yet</p>
            <p class="subtitle">Upload your first document to get started with AI assistance.</p>
            <button class="button is-primary" @click="openUploadModal">
              <font-awesome-icon icon="plus" class="mr-2" />
              Upload Document
            </button>
          </div>

          <div v-else class="table-container">
            <table class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Uploaded By</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="doc in documents" :key="doc.id">
                  <td>
                    <strong>{{ doc.title }}</strong>
                  </td>
                  <td>
                    <span :title="doc.description">
                      {{ truncateText(doc.description, 50) }}
                    </span>
                  </td>
                  <td>
                    <span class="tag is-light">
                      <font-awesome-icon :icon="getFileIcon(doc.file_type)" class="mr-1" />
                      {{ doc.file_type.toUpperCase() }}
                    </span>
                  </td>
                  <td>
                    <span :class="['tag', doc.is_active ? 'is-success' : 'is-danger']">
                      {{ doc.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>{{ doc.uploaded_by_name }}</td>
                  <td>{{ formatDate(doc.created_at) }}</td>
                  <td>
                    <div class="buttons are-small">
                      <button 
                        class="button is-info" 
                        @click="downloadDocument(doc)"
                        title="Download"
                      >
                        <font-awesome-icon icon="download" />
                      </button>
                      <button 
                        class="button is-primary" 
                        @click="editDocument(doc)"
                        title="Edit"
                      >
                        <font-awesome-icon icon="edit" />
                      </button>
                      <button 
                        class="button is-warning" 
                        @click="toggleDocumentStatus(doc)"
                        :title="doc.is_active ? 'Deactivate' : 'Activate'"
                      >
                        <font-awesome-icon :icon="doc.is_active ? 'eye-slash' : 'eye'" />
                      </button>
                      <button 
                        class="button is-danger" 
                        @click="confirmDeleteDocument(doc)"
                        title="Delete"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <nav v-if="pagination.count > pagination.page_size" class="pagination is-centered mt-4">
            <button 
              class="pagination-previous" 
              @click="changePage(pagination.page - 1)"
              :disabled="!pagination.previous"
            >
              Previous
            </button>
            <button 
              class="pagination-next" 
              @click="changePage(pagination.page + 1)"
              :disabled="!pagination.next"
            >
              Next
            </button>
            <ul class="pagination-list">
              <li v-for="page in visiblePages" :key="page">
                <button 
                  :class="['pagination-link', { 'is-current': page === pagination.page }]"
                  @click="changePage(page)"
                >
                  {{ page }}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Upload/Edit Modal -->
    <div class="modal" :class="{ 'is-active': showUploadModal }">
      <div class="modal-background" @click="closeUploadModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ editingDocument ? 'Edit Document' : 'Upload New Document' }}
          </p>
          <button class="delete" @click="closeUploadModal"></button>
        </header>
        <section class="modal-card-body">
          <form @submit.prevent="submitDocument">
            <div class="field">
              <label class="label">Title *</label>
              <div class="control">
                <input 
                  v-model="documentForm.title" 
                  class="input" 
                  type="text" 
                  placeholder="Enter document title"
                  required
                />
              </div>
            </div>

            <div class="field">
              <label class="label">Description</label>
              <div class="control">
                <textarea 
                  v-model="documentForm.description" 
                  class="textarea" 
                  placeholder="Enter document description"
                  rows="3"
                ></textarea>
              </div>
            </div>

            <div class="field">
              <label class="label">File Type *</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="documentForm.file_type" required>
                    <option value="">Select file type</option>
                    <option value="pdf">PDF Document</option>
                    <option value="txt">Text File</option>
                    <option value="docx">Word Document</option>
                    <option value="md">Markdown File</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label">
                {{ editingDocument ? 'Replace File (Optional)' : 'File *' }}
              </label>
              <div class="control">
                <div class="file has-name is-fullwidth">
                  <label class="file-label">
                    <input 
                      class="file-input" 
                      type="file" 
                      @change="handleFileSelect"
                      :required="!editingDocument"
                      accept=".pdf,.txt,.docx,.md"
                    />
                    <span class="file-cta">
                      <span class="file-icon">
                        <font-awesome-icon icon="upload" />
                      </span>
                      <span class="file-label">Choose a file…</span>
                    </span>
                    <span class="file-name">
                      {{ selectedFileName || 'No file selected' }}
                    </span>
                  </label>
                </div>
              </div>
              <p class="help">Supported formats: PDF, TXT, DOCX, MD (Max 10MB)</p>
            </div>

            <div class="field" v-if="editingDocument">
              <label class="checkbox">
                <input type="checkbox" v-model="documentForm.is_active" />
                Document is active
              </label>
            </div>
          </form>
        </section>
        <footer class="modal-card-foot">
          <button 
            class="button is-primary" 
            @click="submitDocument"
            :class="{ 'is-loading': uploading }"
            :disabled="uploading"
          >
            {{ editingDocument ? 'Update' : 'Upload' }}
          </button>
          <button class="button" @click="closeUploadModal">Cancel</button>
        </footer>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" :class="{ 'is-active': showDeleteModal }">
      <div class="modal-background" @click="cancelDelete"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Delete</p>
          <button class="delete" @click="cancelDelete"></button>
        </header>
        <section class="modal-card-body">
          <p>Are you sure you want to delete "<strong>{{ documentToDelete?.title }}</strong>"?</p>
          <p class="has-text-danger mt-2">This action cannot be undone.</p>
        </section>
        <footer class="modal-card-foot">
          <button 
            class="button is-danger" 
            @click="deleteDocument"
            :class="{ 'is-loading': deleting }"
            :disabled="deleting"
          >
            Delete
          </button>
          <button class="button" @click="cancelDelete">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
  faPlus, faSyncAlt, faChartBar, faDatabase, faFileAlt, faFolderOpen,
  faDownload, faEdit, faEye, faEyeSlash, faTrash, faUpload, faSpinner,
  faCheckCircle, faTimesCircle, faFilePdf, faFileWord, faFileText
} from '@fortawesome/free-solid-svg-icons';
import { chatbotService } from '@/api/chatbotApi';
import chatbotApi from '@/api/chatbotApi';
import { toast } from 'bulma-toast';

library.add(
  faPlus, faSyncAlt, faChartBar, faDatabase, faFileAlt, faFolderOpen,
  faDownload, faEdit, faEye, faEyeSlash, faTrash, faUpload, faSpinner,
  faCheckCircle, faTimesCircle, faFilePdf, faFileWord, faFileText
);

export default {
  name: 'KnowledgeBaseManager',
  components: {
    FontAwesomeIcon
  },
  data() {
    return {
      documents: [],
      loading: true,
      showUploadModal: false,
      showDeleteModal: false,
      uploading: false,
      deleting: false,
      rebuildingIndex: false,
      editingDocument: null,
      documentToDelete: null,
      selectedFile: null,
      selectedFileName: '',
      indexStats: null,
      pagination: {
        count: 0,
        page: 1,
        page_size: 10,
        next: null,
        previous: null
      },
      documentForm: {
        title: '',
        description: '',
        file_type: '',
        is_active: true
      }
    };
  },
  computed: {
    visiblePages() {
      const totalPages = Math.ceil(this.pagination.count / this.pagination.page_size);
      const current = this.pagination.page;
      const pages = [];
      
      for (let i = Math.max(1, current - 2); i <= Math.min(totalPages, current + 2); i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  async mounted() {
    await this.fetchDocuments();
    await this.checkIndexStats();
  },
  methods: {
    async fetchDocuments(page = 1) {
      this.loading = true;
      try {
        const response = await chatbotService.getKnowledgeBase();
        this.documents = response.results || [];
        this.pagination = {
          count: response.count || 0,
          page: page,
          page_size: 10,
          next: response.next,
          previous: response.previous
        };
      } catch (error) {
        console.error('Error fetching documents:', error);
        this.showError('Failed to load documents');
      } finally {
        this.loading = false;
      }
    },

    async checkIndexStats() {
      try {
        this.indexStats = await chatbotService.getIndexStats();
      } catch (error) {
        console.error('Error fetching index stats:', error);
      }
    },

    async rebuildIndex() {
      this.rebuildingIndex = true;
      try {
        await chatbotService.rebuildIndex();
        this.showSuccess('Index rebuilt successfully!');
        await this.checkIndexStats();
      } catch (error) {
        console.error('Error rebuilding index:', error);
        this.showError('Failed to rebuild index');
      } finally {
        this.rebuildingIndex = false;
      }
    },

    openUploadModal() {
      this.resetForm();
      this.editingDocument = null;
      this.showUploadModal = true;
    },

    closeUploadModal() {
      this.showUploadModal = false;
      this.resetForm();
    },

    editDocument(doc) {
      this.editingDocument = doc;
      this.documentForm = {
        title: doc.title,
        description: doc.description,
        file_type: doc.file_type,
        is_active: doc.is_active
      };
      this.selectedFile = null;
      this.selectedFileName = '';
      this.showUploadModal = true;
    },

    async submitDocument() {
      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append('title', this.documentForm.title);
        formData.append('description', this.documentForm.description);
        formData.append('file_type', this.documentForm.file_type);
        
        if (this.editingDocument) {
          formData.append('is_active', this.documentForm.is_active);
          if (this.selectedFile) {
            formData.append('file', this.selectedFile);
          }
          await this.updateDocument(this.editingDocument.id, formData);
        } else {
          formData.append('file', this.selectedFile);
          await chatbotService.uploadDocument(formData);
        }

        this.showSuccess(
          this.editingDocument ? 'Document updated successfully!' : 'Document uploaded successfully!'
        );
        this.closeUploadModal();
        await this.fetchDocuments();
        await this.checkIndexStats();
      } catch (error) {
        console.error('Error submitting document:', error);
        this.showError('Failed to save document');
      } finally {
        this.uploading = false;
      }
    },

    async updateDocument(id, formData) {
      // Update document using the chatbot API
      const response = await chatbotApi.patch(`/api/v1/support/knowledge-base/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },

    async toggleDocumentStatus(doc) {
      try {
        await this.updateDocument(doc.id, { is_active: !doc.is_active });
        this.showSuccess(`Document ${doc.is_active ? 'deactivated' : 'activated'} successfully!`);
        await this.fetchDocuments();
      } catch (error) {
        console.error('Error toggling document status:', error);
        this.showError('Failed to update document status');
      }
    },

    confirmDeleteDocument(doc) {
      this.documentToDelete = doc;
      this.showDeleteModal = true;
    },

    async deleteDocument() {
      this.deleting = true;
      try {
        await chatbotApi.delete(`/api/v1/support/knowledge-base/${this.documentToDelete.id}/`);
        this.showSuccess('Document deleted successfully!');
        this.cancelDelete();
        await this.fetchDocuments();
        await this.checkIndexStats();
      } catch (error) {
        console.error('Error deleting document:', error);
        this.showError('Failed to delete document');
      } finally {
        this.deleting = false;
      }
    },

    cancelDelete() {
      this.showDeleteModal = false;
      this.documentToDelete = null;
    },

    downloadDocument(doc) {
      if (doc.file) {
        window.open(doc.file, '_blank');
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          this.showError('File size must be less than 10MB');
          return;
        }
        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    },

    changePage(page) {
      this.fetchDocuments(page);
    },

    resetForm() {
      this.documentForm = {
        title: '',
        description: '',
        file_type: '',
        is_active: true
      };
      this.selectedFile = null;
      this.selectedFileName = '';
    },

    getFileIcon(fileType) {
      switch (fileType.toLowerCase()) {
        case 'pdf': return 'file-pdf';
        case 'docx': return 'file-word';
        case 'txt':
        case 'md': return 'file-text';
        default: return 'file-alt';
      }
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },

    showSuccess(message) {
      toast({
        message,
        type: 'is-success',
        dismissible: true,
        duration: 5000,
        position: 'top-right'
      });
    },

    showError(message) {
      toast({
        message,
        type: 'is-danger',
        dismissible: true,
        duration: 5000,
        position: 'top-right'
      });
    }
  }
};
</script>

<style scoped>
.knowledge-base-manager {
  padding: 20px;
}

.table td {
  vertical-align: middle;
}

.file-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.buttons .button {
  margin-right: 0.25rem;
}

.pagination-link.is-current {
  background-color: #007bff;
  border-color: #007bff;
}

.modal-card {
  width: 600px;
  max-width: 90vw;
}

.help {
  font-size: 0.75rem;
}
</style>
